import { MouseEvent, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';

import { IKnmCheckSubjectResult } from 'APIPackage/KnmModule/interfaces/IKnmCheckSubjectResult';
import { IconCheck } from 'UIPackage/IconsModule/components/IconCheck';
import {
  ROUTE_REGISTRY_TOSER_BY_ID,
  ROUTE_REGISTRY_SPV_BY_ID,
  ROUTE_REGISTRY_EGRUL_BY_ID,
  ROUTE_REGISTRY_MSP_BY_ID,
  ROUTE_REGISTRY_OEZ_BY_ID,
  ROUTE_REGISTRY_EGRIP_BY_ID,
} from 'CorePackage/MainModule/routes/routes';
import {
  getKnmDataId,
  getKnmDataGuid,
  getKnmDataCheckSubjectResult,
  getKnmDataIsIndividualEntrepreneur,
  getKnmDataIsLegalEntity,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';
import { useRegistryCheck } from 'KnmPackage/MainModule/hooks/useRegistryCheck';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import styles from './KnmErpRegistryCheck.module.scss';

enum CheckSubjectStatesEnum {
  NOT_RUN = 'NOT_RUN',
  RUN = 'RUN',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

function getCheckSubjectState(checkSubjectResult?: IKnmCheckSubjectResult) {
  if (checkSubjectResult && checkSubjectResult.requestTime) {
    if (typeof checkSubjectResult.egrip?.id === 'number' || typeof checkSubjectResult?.egrul?.id === 'number') {
      return CheckSubjectStatesEnum.SUCCESS;
    }

    return CheckSubjectStatesEnum.RUN;
    // return CheckSubjectStatesEnum.ERROR; // ToDo
  }

  return CheckSubjectStatesEnum.NOT_RUN;
}

interface IKnmErpRegistryCheckProps {
  newKnm: boolean;
  inn: string | undefined;
  ogrn: string | undefined;
}

interface IKnmErpRegistryCheckStoreProps {
  knmId: number | undefined;
  guid: string | null | undefined;
  initialCheckSubjectResult: IKnmCheckSubjectResult | null | undefined;
  isIndividualEntrepreneur: boolean | null | undefined;
  isLegalEntity: boolean | null | undefined;
}

const KnmErpRegistryCheckWithoutStore = ({
  newKnm,
  inn,
  ogrn,
  guid,
  initialCheckSubjectResult,
  isIndividualEntrepreneur,
  isLegalEntity,
  knmId,
}: IKnmErpRegistryCheckProps & IKnmErpRegistryCheckStoreProps) => {
  const { isLoading: isRegistryCheckLoading, checkSubjectResult, registryCheck } = useRegistryCheck(
    knmId,
    initialCheckSubjectResult || undefined,
  );
  const checkSubjectState: CheckSubjectStatesEnum = getCheckSubjectState(checkSubjectResult);

  const checkRegistryHandler = useCallback(async () => {
    await registryCheck(guid || undefined, inn, ogrn);
  }, [guid, inn, ogrn, registryCheck]);

  const handleRegistryClick = (id?: number | null) => (event: MouseEvent<HTMLAnchorElement>) => {
    if (typeof id !== 'number') {
      event.preventDefault();
    }
  };

  const renderTooltip = () => {
    if (checkSubjectState === CheckSubjectStatesEnum.RUN) {
      return (
        <Tooltip
          overlayClassName={styles.CheckIconTooltip}
          placement="bottom"
          content={<div className={styles.CheckIconTooltipContent}>Идет проверка</div>}
        >
          <span>
            <IconCheck className={styles.OrangeCheckIcon} />
          </span>
        </Tooltip>
      );
    }
    if (checkSubjectState === CheckSubjectStatesEnum.SUCCESS) {
      return (
        <Tooltip
          overlayClassName={styles.CheckIconTooltip}
          placement="bottom"
          content={<div className={styles.CheckIconTooltipContent}>Проверка проводилась</div>}
        >
          <span>
            <IconCheck className={styles.GreenCheckIcon} />
          </span>
        </Tooltip>
      );
    }
    // ToDo
    // if (checkSubjectState === CheckSubjectStatesEnum.ERROR) {
    //   return (
    //     <Tooltip
    //       overlayClassName={styles.CheckIconTooltip}
    //       placement="bottom"
    //       content={
    //         <div className={styles.CheckIconTooltipContent}>
    //           Ошибка обращения к сервису. Попробуйте повторить запрос позже.
    //         </div>
    //       }
    //     >
    //       <span>
    //         <IconCheck className={styles.RedCheckIcon} />
    //       </span>
    //     </Tooltip>
    //   );
    // }

    return (
      <Tooltip
        overlayClassName={styles.CheckIconTooltip}
        placement="bottom"
        content={<div className={styles.CheckIconTooltipContent}>Проверка не проводилась</div>}
      >
        <span>
          <IconCheck className={styles.GrayCheckIcon} />
        </span>
      </Tooltip>
    );
  };

  const linkEgrulAttributes = {
    ...(typeof checkSubjectResult?.egrul?.id !== 'number' && { 'data-disabled': true }),
    ...(typeof checkSubjectResult?.egrul?.id === 'number' && { 'data-enabled': true }),
  };

  const linkEgripAttributes = {
    ...(typeof checkSubjectResult?.egrip?.id !== 'number' && { 'data-disabled': true }),
    ...(typeof checkSubjectResult?.egrip?.id === 'number' && { 'data-enabled': true }),
  };

  const linkMspAttributes = {
    ...(typeof checkSubjectResult?.msp?.id !== 'number' && { 'data-disabled': true }),
    ...(typeof checkSubjectResult?.msp?.id === 'number' && { 'data-enabled': true }),
  };

  const linkToserAttributes = {
    ...(typeof checkSubjectResult?.toser?.id !== 'number' && { 'data-disabled': true }),
    ...(typeof checkSubjectResult?.toser?.id === 'number' && { 'data-enabled': true }),
  };

  const linkSpvAttributes = {
    ...(typeof checkSubjectResult?.spv?.id !== 'number' && { 'data-disabled': true }),
    ...(typeof checkSubjectResult?.spv?.id === 'number' && { 'data-enabled': true }),
  };

  const linkOezAttributes = {
    ...(typeof checkSubjectResult?.oez?.id !== 'number' && { 'data-disabled': true }),
    ...(typeof checkSubjectResult?.spv?.id === 'number' && { 'data-enabled': true }),
  };

  return (
    <div className={styles.Wrapper}>
      <ul className={styles.StatementList}>
        {(checkSubjectState === CheckSubjectStatesEnum.NOT_RUN ||
          checkSubjectState === CheckSubjectStatesEnum.RUN ||
          isLegalEntity ||
          typeof checkSubjectResult?.egrul?.id === 'number') && (
          <li className={styles.StatementItem}>
            <Link
              className={styles.Link}
              to={
                typeof checkSubjectResult?.egrul?.id === 'number'
                  ? ROUTE_REGISTRY_EGRUL_BY_ID(checkSubjectResult.egrul.id)
                  : '#'
              }
              target="_blank"
              onClick={handleRegistryClick(checkSubjectResult?.egrul?.id)}
              {...linkEgrulAttributes}
            >
              Выписка из ЕГРЮЛ
            </Link>
          </li>
        )}
        {(isIndividualEntrepreneur || typeof checkSubjectResult?.egrip?.id === 'number') && (
          <li className={styles.StatementItem}>
            <Link
              className={styles.Link}
              to={
                typeof checkSubjectResult?.egrip?.id === 'number'
                  ? ROUTE_REGISTRY_EGRIP_BY_ID(checkSubjectResult.egrip.id)
                  : '#'
              }
              target="_blank"
              onClick={handleRegistryClick(checkSubjectResult?.egrip?.id)}
              {...linkEgripAttributes}
            >
              Выписка из ЕГРИП
            </Link>
          </li>
        )}
        <li className={styles.StatementItem}>
          <Link
            className={styles.Link}
            to={
              typeof checkSubjectResult?.msp?.id === 'number'
                ? ROUTE_REGISTRY_MSP_BY_ID(checkSubjectResult.msp.id)
                : '#'
            }
            target="_blank"
            onClick={handleRegistryClick(checkSubjectResult?.msp?.id)}
            {...linkMspAttributes}
          >
            Выписка из МСП
          </Link>
        </li>
        <li className={styles.StatementItem}>
          <Link
            className={styles.Link}
            to={
              typeof checkSubjectResult?.toser?.id === 'number'
                ? ROUTE_REGISTRY_TOSER_BY_ID(checkSubjectResult.toser.id)
                : '#'
            }
            target="_blank"
            onClick={handleRegistryClick(checkSubjectResult?.toser?.id)}
            {...linkToserAttributes}
          >
            Выписка из реестра ТОСЭР
          </Link>
        </li>
        <li className={styles.StatementItem}>
          <Link
            className={styles.Link}
            to={
              typeof checkSubjectResult?.spv?.id === 'number'
                ? ROUTE_REGISTRY_SPV_BY_ID(checkSubjectResult.spv.id)
                : '#'
            }
            target="_blank"
            onClick={handleRegistryClick(checkSubjectResult?.spv?.id)}
            {...linkSpvAttributes}
          >
            Выписка из реестра СПВ
          </Link>
        </li>
        <li className={styles.StatementItem}>
          <Link
            className={styles.Link}
            to={
              typeof checkSubjectResult?.oez?.id === 'number'
                ? ROUTE_REGISTRY_OEZ_BY_ID(checkSubjectResult.oez.id)
                : '#'
            }
            target="_blank"
            onClick={handleRegistryClick(checkSubjectResult?.oez?.id)}
            {...linkOezAttributes}
          >
            Выписка из реестра ОЭЗ
          </Link>
        </li>
      </ul>

      <div className={styles.Check}>
        <Button
          text="Проверить данные"
          className={styles.ButtonCheck}
          color="primary"
          size="medium"
          type="button"
          disabled={newKnm || !inn || !ogrn}
          onClick={checkRegistryHandler}
        />
        {isRegistryCheckLoading ? <div className={styles.Loader} /> : renderTooltip()}
      </div>
    </div>
  );
};

const mapStateToProps = (state: IRootState): IKnmErpRegistryCheckStoreProps => ({
  knmId: getKnmDataId(state),
  guid: getKnmDataGuid(state),
  initialCheckSubjectResult: getKnmDataCheckSubjectResult(state),
  isIndividualEntrepreneur: getKnmDataIsIndividualEntrepreneur(state),
  isLegalEntity: getKnmDataIsLegalEntity(state),
});

export const KnmErpRegistryCheck = connect(mapStateToProps)(KnmErpRegistryCheckWithoutStore);
